<template>
  <div v-if="order" class="rd-gp">
    <div class="rd-gp__top">
      <span class="rd-sbadge rd-sbadge--static" :style="chipStyle(currentState)">
        <i class="rd-dot" :style="dotStyle(currentState)"></i>
        {{ $t(currentState) }}
      </span>
      <span class="rd-muted rd-num">{{ formatDateTime(order.created_at) }}</span>
      <span class="rd-gp__spacer"></span>
      <button type="button" class="ui-btn ui-btn--sm" @click="printOrder">
        <v-icon size="16">mdi-printer-outline</v-icon>
        {{ $t("print") }}
      </button>
    </div>

    <div class="rd-steps" role="group" :aria-label="$t('status')">
      <button
        v-for="item in orderStatuses"
        :key="item.value"
        type="button"
        class="rd-step"
        :aria-pressed="String(currentState === item.value)"
        :disabled="busy"
        :style="stepStyle(item.value)"
        @click="handleStatusClick(item.value)"
      >
        <v-icon size="14">{{ item.icon }}</v-icon>
        {{ $t(item.value) }}
      </button>
    </div>

    <section class="rd-gp__card">
      <div class="rd-person">
        <span class="ui-avatar ui-avatar--neutral ui-avatar--lg">{{ initials }}</span>
        <span class="rd-gp__who">
          <b>{{ guestName }}</b>
          <a v-if="guest && guest.phone" class="rd-gp__phone rd-num" :href="`tel:${guest.phone}`">
            <v-icon size="14" color="#c2410c">mdi-phone</v-icon>
            {{ guest.phone }}
          </a>
          <span v-if="guest && guest.email" class="rd-muted">{{ guest.email }}</span>
        </span>
      </div>
      <span class="ui-chip ui-chip--tag rd-tag">
        <v-icon size="14">{{ deliveryType.icon }}</v-icon>
        {{ deliveryType.label }}
      </span>
    </section>

    <section v-if="order.delivery_type === 0" class="rd-gp__card rd-gp__card--col">
      <span class="rd-gp__label">
        <v-icon size="14">mdi-map-marker-outline</v-icon>
        {{ $t("delivery_address") }}
      </span>
      <template v-if="order.location">
        <b>{{ order.location.address }}</b>
        <span class="rd-muted">
          {{ order.location.city ? order.location.city.name : "" }}
          <template v-if="order.location.apartment"> · {{ $t("apartment") }}: {{ order.location.apartment }}</template>
          <template v-if="order.location.floor"> · {{ $t("floor") }}: {{ order.location.floor }}</template>
        </span>
      </template>
      <span v-else class="rd-muted">–</span>
    </section>

    <section class="rd-gp__card rd-gp__times">
      <div>
        <span class="rd-gp__label">{{ $t("ordered") }}</span>
        <b class="rd-num">{{ formatDateTime(order.created_at) }}</b>
      </div>
      <div>
        <span class="rd-gp__label">{{ $t("deliver_until") }}</span>
        <b class="rd-num rd-gp__due">
          {{ formatScheduledDate(order.date) }}
          {{ order.delivery_time ? order.delivery_time.substring(0, 5) : "–" }}
        </b>
      </div>
    </section>

    <section class="rd-gp__card rd-gp__card--col">
      <span class="rd-gp__label">
        <v-icon size="14">mdi-food-outline</v-icon>
        {{ $t("ordered") }}
      </span>
      <ul class="rd-gp__items">
        <li
          v-for="(line, index) in orderLines"
          :key="line.id || index"
          :class="{ 'is-choice': line.line_type === 'choice', 'is-package': line.line_type === 'package' }"
        >
          <b v-if="line.line_type !== 'choice'" class="rd-gp__qty rd-num">{{ line.quantity }}×</b>
          <v-icon v-else size="14" color="#8a817a">mdi-subdirectory-arrow-right</v-icon>
          <span class="rd-gp__item">
            <template v-if="line.line_type === 'choice' && line.quantity > 1">{{ line.quantity }}× </template>
            {{ toLatin(line.food ? line.food.name : line.meals ? line.meals.name : "") }}
          </span>
          <span v-if="line.line_type !== 'choice'" class="rd-num">
            {{ (line.price * line.quantity).toFixed(2) }} {{ orderCurrency }}
          </span>
          <span v-else-if="Number(line.price) > 0" class="rd-muted rd-num">
            +{{ (line.price * line.quantity).toFixed(2) }} {{ orderCurrency }}
          </span>
        </li>
      </ul>
    </section>

    <div class="rd-gp__total">
      <span>{{ $t("total") }}</span>
      <b class="rd-num">{{ calculateOrderTotal.toFixed(2) }} {{ orderCurrency }}</b>
    </div>

    <prep-time-dialog v-model="prepTimeDialog" @select-minutes="confirmPrepMinutes" @ready-now="confirmReadyNow"></prep-time-dialog>
  </div>
</template>

<script>
import GuestOrderDetailsDialog from "@/components/dialogs/GuestOrderDetailsDialog.vue";
import { DELIVERY_TYPES, getStatusColors } from "@/utils/guestOrderStatus";
import reportService from "@/services/report";

/**
 * Detalji narudžbe u panelu sa desne strane: status (promena jednim klikom), gost, adresa, vreme,
 * stavke sa izborima iz paketa i ukupno. Nasleđuje iz starog dijaloga stavke, ukupno, štampu
 * narudžbe i formatiranje, a promenu statusa prosleđuje ekranu koji je otvorio panel (`owner`).
 */
export default {
  name: "GuestOrderPanel",
  extends: GuestOrderDetailsDialog,
  props: {
    /** Ekran (GuestDesktopOrders) koji menja status i zna da li je promena u toku */
    owner: { type: Object, default: null },
  },
  computed: {
    busy() {
      return Boolean(this.owner && this.order && this.owner.changingStatusOrderId === this.order.id);
    },
    guestName() {
      return this.guest ? `${this.toLatin(this.guest.name || "")} ${this.toLatin(this.guest.lastname || "")}`.trim() : "";
    },
    initials() {
      return [this.guest?.name, this.guest?.lastname]
        .filter(Boolean)
        .map((part) => this.toLatin(part).charAt(0))
        .join("");
    },
    deliveryType() {
      const type = DELIVERY_TYPES.find((item) => item.id === this.order?.delivery_type);
      return type ? { icon: type.icon, label: this.$t(type.key) } : { icon: "mdi-help-circle-outline", label: "" };
    },
  },
  methods: {
    chipStyle(state) {
      const colors = getStatusColors(state);
      return { backgroundColor: colors.bg, color: colors.fg };
    },
    dotStyle(state) {
      return { backgroundColor: getStatusColors(state).fg };
    },
    stepStyle(state) {
      if (this.currentState !== state) {
        return {};
      }
      const colors = getStatusColors(state);
      return { backgroundColor: colors.bg, color: colors.fg, borderColor: colors.fg };
    },
    /** "Prihvaćeno" prvo pita za vreme pripreme (kao na kuhinjskom ekranu); ostalo ide odmah. */
    handleStatusClick(newStatus) {
      if (newStatus === this.currentState) {
        return;
      }
      if (newStatus === "accepted") {
        this.prepTimeDialog = true;
        return;
      }
      this.apply(newStatus);
    },
    // minuti pripreme idu uz prihvatanje kao vreme spremnosti (vidi reportService.rememberPrepMinutes)
    confirmPrepMinutes(minutes) {
      this.prepTimeDialog = false;
      reportService.rememberPrepMinutes(this.order.id, minutes);
      this.apply("accepted");
    },
    confirmReadyNow() {
      this.prepTimeDialog = false;
      this.apply("ready");
    },
    apply(newStatus) {
      if (this.owner) {
        this.owner.changeOrderStatus(this.order, this.guest, newStatus);
      }
    },
  },
};
</script>
